import React, {useCallback, useMemo, useRef} from 'react';
import {memoize} from 'lodash';

import {
  ConstNode,
  Node,
  NodeOrVoidNode,
  OpStore,
  ServerOpDef,
  Type,
  WeaveInterface,
  callOpVeryUnsafe,
  constNodeUnsafe,
  isFunction,
  loadRemoteOpStore,
  varNode,
  voidNode,
} from '@wandb/cg';
import {backendWeaveOpsUrl} from '@wandb/common/config';
import {useGatedValue} from '../../hookUtils';

import {useNodeValue} from '../../cgreact';
import {useWeaveContext} from '../../context';
import {registerPanel} from './PanelRegistry';
import * as Panel2 from './panel';
import {Panel, Panel2Loader} from './PanelComp';
import {ExpressionEvent, PanelContextProvider} from './PanelContext';

// This file contains the implementation of a
// "pure weave panel", ie one that is defined entirely in terms of
// weave objects, with no corresponding React components.
// import {constNodeUnsafe} from '@wandb/cg/browser/basicNodes';
import {toWeaveType} from './PanelGroup';
import {consoleWarn} from '../../util';

interface UserPanelConfig {
  // The panel returned by the panel's config op.
  _configRenderAsPanel?: {
    id: string;
    input_node: Node;
    config: any;
  };
  // The panel returned by the panel's render op.
  _renderAsPanel?: {
    id: string;
    input_node: Node;
    config: any;
  };
  [key: string]: any;
}
type UserPanelProps = Panel2.PanelProps<'any', UserPanelConfig>;

const useUserPanelVars = (
  props: UserPanelProps,
  panelInputName: string,
  panelInputNodeType: Type,
  opConfigType: Type,
  renderOpName: string,
  renderAsPanelConfigAttr: '_renderAsPanel' | '_configRenderAsPanel',
  weave: WeaveInterface,
  skip: boolean = false
) => {
  const {config, updateConfig, updateConfig2} = props;
  const initialLoading = useRef(true);

  // We can figure out the type as a TypedDict
  // but we want to send it back as the actual ObjectType!
  const configTypedDictType = toWeaveType(config);
  const configType = useMemo(
    () => ({
      ...(opConfigType as any),
      ...configTypedDictType.propertyTypes,
    }),
    [configTypedDictType.propertyTypes, opConfigType]
  );
  const configConstNode = constNodeUnsafe(
    config == null ? 'none' : configType,
    config ?? null
  );
  const panelVars = useMemo(() => {
    const v: {[key: string]: Node} = {
      [panelInputName]: props.input,
    };
    if (opConfigType !== 'none') {
      v.config = configConstNode;
    }
    return v;
  }, [configConstNode, opConfigType, panelInputName, props.input]);

  const handleVarEvent = useCallback(
    (varName: string, target: NodeOrVoidNode, event: ExpressionEvent) => {
      console.log('HANDLE USER PANEL VAR EVENT', varName, event);
      if (event.id === 'mutate') {
        if (varName === 'config') {
          if (updateConfig2 != null) {
            // TODO(np): newRootNode guaranteed to be ConstNode here?
            updateConfig2(() => (event.newRootNode as ConstNode).val);
          }
          return;
        }
      }
      consoleWarn('UserPanel Unhandled var event', varName, event, target);
    },
    [updateConfig2]
  );

  // const configType = toWeaveType(props.config);
  const calledRender = useMemo(() => {
    const renderOpArgs: {[key: string]: Node} = {
      [panelInputName]: {
        nodeType: 'const' as const,
        type: {
          type: 'function' as const,
          inputTypes: {},
          outputType: panelInputNodeType,
        },
        val: varNode(panelInputNodeType, panelInputName),
      },
    };
    if (opConfigType !== 'none') {
      renderOpArgs.config = {
        nodeType: 'const' as const,
        type: {
          type: 'function' as const,
          inputTypes: {},
          outputType: configType,
        },
        val: varNode(configType, 'config'),
      };
    }
    return !skip && props.config?.[renderAsPanelConfigAttr] == null
      ? (callOpVeryUnsafe(renderOpName, renderOpArgs) as Node)
      : voidNode();
  }, [
    panelInputName,
    panelInputNodeType,
    opConfigType,
    configType,
    skip,
    props.config,
    renderAsPanelConfigAttr,
    renderOpName,
  ]);
  console.log('CALLED RENDER', calledRender);
  let renderOpResult = useNodeValue(calledRender);
  // If the render op is still loading, use the previous rendered panel
  renderOpResult = useGatedValue(renderOpResult, v => !v.loading);
  if (!renderOpResult.loading) {
    initialLoading.current = false;
  }

  const renderAsPanel = useMemo(
    () =>
      // If the panel has been modified in the UI, use the modified panel config
      // instead of what would be returned by the render op
      props.config?.[renderAsPanelConfigAttr] ??
      (renderOpResult.loading
        ? {
            id: '',
            input_node: voidNode(),
            config: undefined,
          }
        : {
            id: renderOpResult.result.id,
            input_node: renderOpResult.result.input_node,
            config: renderOpResult.result.config,
          }),
    [props.config, renderAsPanelConfigAttr, renderOpResult]
  );

  const updatePanelConfig = useCallback(
    (newPanelConfig: any) => {
      updateConfig({
        [renderAsPanelConfigAttr]: {
          id: renderAsPanel.id,
          input_node: renderAsPanel.input_node,
          config: {...renderAsPanel.config, ...newPanelConfig},
        },
      });
    },
    [
      updateConfig,
      renderAsPanelConfigAttr,
      renderAsPanel.id,
      renderAsPanel.input_node,
      renderAsPanel.config,
    ]
  );
  const updatePanelConfig2 = useCallback(
    (change: <T>(oldConfig: T) => Partial<T>) => {
      if (updateConfig2 == null) {
        return;
      }
      updateConfig2((oldConfig: UserPanelConfig): UserPanelConfig => {
        const oldRenderAsPanel = oldConfig[renderAsPanelConfigAttr] ?? {
          id: renderOpResult.result.id,
          input_node: renderOpResult.result.input_node,
          config: renderOpResult.result.config,
        };
        return {
          [renderAsPanelConfigAttr]: {
            id: oldRenderAsPanel.id,
            input_node: oldRenderAsPanel.input_node,
            config: {
              ...oldRenderAsPanel.config,
              ...change(oldConfig[renderAsPanelConfigAttr]),
            },
          },
        };
      });
    },
    [renderAsPanelConfigAttr, renderOpResult.result, updateConfig2]
  );

  // We shouldn't need to do the dereferencing here. It should happen in
  // the framework, right before execution time (maybe in useNodeValue).
  // But doing it more broadly will need some good testing to make sure
  // we don't breaking anything.
  // TODO: fix
  const inputNode = useMemo(
    () =>
      weave.callFunction(renderAsPanel.input_node, {
        [panelInputName]: props.input,
      }),
    [panelInputName, props.input, renderAsPanel.input_node, weave]
  );
  return useMemo(
    () => ({
      renderAsPanel,
      panelVars,
      handleVarEvent,
      updatePanelConfig,
      updatePanelConfig2,
      modified: props.config?.[renderAsPanelConfigAttr] != null,
      inputNode,
      loading: initialLoading.current,
    }),
    [
      renderAsPanel,
      panelVars,
      handleVarEvent,
      updatePanelConfig,
      updatePanelConfig2,
      props.config,
      renderAsPanelConfigAttr,
      inputNode,
    ]
  );
};

const registerUserPanel = (op: ServerOpDef, configOp?: ServerOpDef) => {
  const inputNames = Object.keys(op.input_types);
  const inputTypes = Object.values(op.input_types);
  const opInputName0 = inputNames[0];
  if (!isFunction(inputTypes[0])) {
    consoleWarn('non-function type for panel op: ', op);
    return;
  }
  const inputType = inputTypes[0].outputType;

  if (inputNames[1] != null && inputNames[1] !== 'config') {
    consoleWarn('panel op 2nd arg name is not "config": ', op);
    return;
  }
  const configPropertyTypes: {[key: string]: Type} = {};
  let opConfigType: Type = 'none';
  if (inputTypes[1] != null) {
    opConfigType = inputTypes[1];
    for (const [name, type] of Object.entries(opConfigType)) {
      if (name !== 'type' && name !== '_is_object' && name !== '_base_type') {
        configPropertyTypes[name] = type;
      }
    }
    if (!(inputTypes[1] as any)._is_object) {
      consoleWarn('panel op config type must be ObjectType: ', op);
    }
  }

  const ConfigComponent: React.FC<UserPanelProps> | undefined =
    configOp == null
      ? undefined
      : props => {
          const weave = useWeaveContext();
          const {
            panelVars,
            handleVarEvent,
            renderAsPanel,
            updatePanelConfig,
            updatePanelConfig2,
            loading,
            inputNode,
          } = useUserPanelVars(
            props,
            opInputName0,
            props.input.type,
            opConfigType,
            configOp.name,
            '_configRenderAsPanel',
            weave
          );
          if (loading) {
            return <Panel2Loader />;
          }
          console.log('CONFIG RENDER AS PANEL', renderAsPanel);
          return (
            <PanelContextProvider
              newVars={panelVars}
              handleVarEvent={handleVarEvent}>
              <Panel
                panelSpec={renderAsPanel.id}
                input={inputNode}
                config={renderAsPanel.config}
                updateConfig={updatePanelConfig}
                updateConfig2={updatePanelConfig2}
                updateInput={props.updateInput}
              />
            </PanelContextProvider>
          );
        };
  const RenderComponent: React.FC<UserPanelProps> = props => {
    const weave = useWeaveContext();
    const {
      panelVars,
      handleVarEvent,
      renderAsPanel,
      updatePanelConfig,
      updatePanelConfig2,
      loading,
      inputNode,
    } = useUserPanelVars(
      props,
      opInputName0,
      props.input.type,
      opConfigType,
      op.name,
      '_renderAsPanel',
      weave,
      false
    );
    if (loading) {
      return <Panel2Loader />;
    }
    console.log('RENDER AS PANEL', renderAsPanel);
    return (
      <PanelContextProvider newVars={panelVars} handleVarEvent={handleVarEvent}>
        <Panel
          panelSpec={renderAsPanel.id}
          input={inputNode}
          config={renderAsPanel.config}
          updateConfig={updatePanelConfig}
          updateConfig2={updatePanelConfig2}
          updateInput={props.updateInput}
        />
      </PanelContextProvider>
    );
  };

  registerPanel({
    id: op.name,
    ConfigComponent,
    Component: RenderComponent,
    inputType,
  });
};

const loadWeaveObjects = (): Promise<OpStore> => {
  return loadRemoteOpStore(backendWeaveOpsUrl()).then(
    ({remoteOpStore, userPanelOps}) => {
      // For now we use the convention that config op names end with '_config'
      const renderOps: ServerOpDef[] = [];
      const configOps: {[opName: string]: ServerOpDef} = {};
      for (const op of userPanelOps) {
        if (op.name.endsWith('_config')) {
          configOps[op.name] = op;
        } else {
          renderOps.push(op);
        }
      }
      for (const op of renderOps) {
        registerUserPanel(op, configOps[op.name + '_config']);
      }
      return remoteOpStore;
    }
  );
};

const memoedLoadWeaveObject = memoize(loadWeaveObjects);

export const useLoadWeaveObjects = (
  skip?: boolean
):
  | {loading: true; remoteOpStore: OpStore | null}
  | {loading: false; remoteOpStore: OpStore} => {
  const [loading, setLoading] = React.useState(true);
  const [remoteOpStore, setRemoteOpStore] = React.useState<OpStore | null>(
    null
  );

  React.useEffect(() => {
    if (!skip) {
      memoedLoadWeaveObject().then(loadedRemoteOpStore => {
        setRemoteOpStore(loadedRemoteOpStore);
        setLoading(false);
      });
    }
  }, [skip]);

  if (loading || remoteOpStore == null) {
    return {loading: true, remoteOpStore};
  }
  return {loading, remoteOpStore};
};
